import React from 'react';
import { Alert, AlertTitle, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';
import { getDate } from '../../../utils/common';
import { VISIT_TYPE_DISPLAY_NAME_OVERRIDES } from '../consts/visitTypeDisplayNames';

interface VisitDetailsProps {
    visita: any;
}

const hasValue = (value: unknown) => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim().length > 0 && value.trim() !== '-';
    return true;
};

const VisitDetails: React.FC<VisitDetailsProps> = ({ visita }) => {
    const { t } = useTranslation('visitas');

    const visitTypes = Array.isArray(visita?.actividades)
        ? visita.actividades
              .map(
                  (a: any) =>
                      VISIT_TYPE_DISPLAY_NAME_OVERRIDES[a?.tipoVisita?.id] || a?.tipoVisita?.descripcion
              )
              .filter((value: unknown) => hasValue(value))
        : [];

    const details = [
        {
            label: t('drawer.info.contract'),
            value:
                visita?.establecimiento?.organismo?.organismoCentral?.nombre ||
                visita?.establecimiento?.organismoCentral?.nombre
        },
        {
            label: t('drawer.info.organism'),
            value: visita?.establecimiento?.organismo?.nombre
        },
        {
            label: t('drawer.info.establishment'),
            value: visita?.establecimiento?.nombre
        },
        {
            label: t('drawer.info.codigoSRT'),
            value: visita?.establecimiento?.codigoSRT
        },
        {
            label: t('drawer.info.city'),
            value: visita?.establecimiento?.direccion?.localidad?.nombre
        },
        {
            label: t('drawer.info.zipCode'),
            value: visita?.zipCode
        },
        {
            label: t('drawer.info.address'),
            value: [
                visita?.establecimiento?.direccion?.calle,
                visita?.establecimiento?.direccion?.altura
            ]
                .filter((value) => hasValue(value))
                .join(' ')
        },
        {
            label: t('drawer.info.deadline'),
            value: visita?.fechaLimite
                ? /^\d{2}\/\d{2}\/\d{4}$/.test(visita.fechaLimite)
                    ? visita.fechaLimite
                    : getDate(visita.fechaLimite)
                : null
        },
        {
            label: t('drawer.info.observations'),
            value: visita?.observacion
        }
    ].filter((detail) => hasValue(detail.value));

    return (
        <Alert
            severity="info"
            sx={{
                backgroundColor: '#E3F2FD',
                borderLeft: '5px solid #175AB6',
                padding: 2,
                fontWeight: 600,
                mt: 5,
                mb: 5
            }}
            icon={<InfoOutlinedIcon sx={{ color: '#175AB6' }} />}
        >
            <AlertTitle sx={{ fontWeight: 600 }}>{t('drawer.info.alertTitle')}</AlertTitle>

            {details.map((detail) => (
                <Typography key={detail.label} variant="h6">
                    <strong>{detail.label}:</strong> {detail.value}
                </Typography>
            ))}

            {visitTypes.length > 0 && (
                <Typography variant="h6">
                    <strong>{t('drawer.info.visitType')}:</strong>
                    {visitTypes.map((visitType: string, index: number) => (
                        <Typography key={`${visitType}-${index}`} variant="h6" sx={{ ml: 2 }}>
                            - {visitType}
                        </Typography>
                    ))}
                </Typography>
            )}
        </Alert>
    );
};

export default VisitDetails;
